import type {
  BodyIdDto,
  CreateMission,
  LaunchSiteDto,
  MissionType,
  RocketRecord,
} from '@trajectory/contracts';
import { Dialog, NumberField } from '@trajectory/ui';
import {
  BookOpen,
  AlertTriangle,
  Check,
  ChevronLeft,
  ChevronRight,
  CloudSun,
  Compass,
  Flag,
  Package,
  Rocket,
  RefreshCw,
  X,
} from 'lucide-react';
import { useEffect, useMemo, useState, type ReactNode } from 'react';
import type { CelestialBodyDto, OrbitPresetDto } from '@/hooks/useCatalog';
import {
  buildMissionWizardPayload,
  createMissionWizardDraft,
  destinationsForMissionType,
  missionTypeGuidance,
  suggestedDestination,
  validateMissionWizardStep,
  type MissionWizardDraft,
} from '@/lib/mission-wizard';

interface MissionCreationWizardProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  launchSites: LaunchSiteDto[];
  bodies: CelestialBodyDto[];
  orbitPresets: OrbitPresetDto[];
  rockets: RocketRecord[];
  isLoadingCatalog: boolean;
  catalogError?: string;
  onRetryCatalog?: () => void;
  isSubmitting: boolean;
  onSubmit: (payload: CreateMission) => Promise<void>;
}

const steps = [
  { label: 'Purpose', icon: Flag },
  { label: 'Destination', icon: Compass },
  { label: 'Launch', icon: Rocket },
  { label: 'Payload', icon: Package },
  { label: 'Conditions', icon: CloudSun },
  { label: 'Review', icon: Check },
] as const;

const inputClass =
  'w-full rounded-control border border-border bg-surface px-3 py-2 text-sm text-text outline-none focus:border-accent';

function Field({
  label,
  optional,
  hint,
  children,
}: {
  label: string;
  optional?: boolean;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <label className="flex flex-col gap-1.5 text-xs text-muted">
      <span className="flex items-center gap-2">
        <span className="font-medium text-text">{label}</span>
        {optional && (
          <span className="rounded-full bg-surface-sunken px-1.5 py-0.5 text-[10px] text-faint">
            Optional
          </span>
        )}
      </span>
      {children}
      {hint && <span className="leading-relaxed text-faint">{hint}</span>}
    </label>
  );
}

function FieldGroup({
  label,
  hint,
  children,
}: {
  label: string;
  hint?: string;
  children: ReactNode;
}) {
  return (
    <div className="flex flex-col gap-1.5 text-xs text-muted">
      <span className="font-medium text-text">{label}</span>
      {children}
      {hint && <span className="leading-relaxed text-faint">{hint}</span>}
    </div>
  );
}

function LearningNote({ children }: { children: ReactNode }) {
  return (
    <div className="flex gap-2 rounded-panel border border-info/30 bg-info/5 p-3 text-xs leading-relaxed text-muted">
      <BookOpen className="mt-0.5 h-4 w-4 shrink-0 text-info" />
      <div>
        <strong className="text-text">Learner note:</strong> {children}
      </div>
    </div>
  );
}

function SummaryRow({ label, value }: { label: string; value: ReactNode }) {
  return (
    <div className="grid grid-cols-[9rem_1fr] gap-3 border-b border-border py-2 text-xs last:border-0">
      <span className="text-faint">{label}</span>
      <span className="text-text">{value}</span>
    </div>
  );
}

export function MissionCreationWizard({
  open,
  onOpenChange,
  launchSites,
  bodies,
  orbitPresets,
  rockets,
  isLoadingCatalog,
  catalogError,
  onRetryCatalog,
  isSubmitting,
  onSubmit,
}: MissionCreationWizardProps) {
  const [step, setStep] = useState(0);
  const [errors, setErrors] = useState<string[]>([]);
  const [draft, setDraft] = useState<MissionWizardDraft>(() =>
    createMissionWizardDraft(launchSites[0]?.id ?? ''),
  );
  const firstLaunchSiteId = launchSites[0]?.id ?? '';

  useEffect(() => {
    if (!open) return;
    setStep(0);
    setErrors([]);
    setDraft(createMissionWizardDraft(firstLaunchSiteId));
  }, [open, firstLaunchSiteId]);

  const selectedSite = launchSites.find((site) => site.id === draft.launchSiteId);
  const selectedBody = bodies.find((body) => body.id === draft.destinationBody);
  const selectedOrbit = orbitPresets.find((preset) => preset.id === draft.orbitPresetId);
  const selectedRocket = rockets.find((rocket) => rocket.id === draft.rocketId);
  const availableBodies = useMemo(
    () => {
      const supported = destinationsForMissionType(draft.missionType);
      return bodies.filter((body) =>
        (supported as readonly string[]).includes(body.id),
      );
    },
    [bodies, draft.missionType],
  );

  const patch = (update: Partial<MissionWizardDraft>) => {
    setDraft((current) => ({ ...current, ...update }));
    setErrors([]);
  };

  const goNext = () => {
    const nextErrors = validateMissionWizardStep(draft, step, launchSites);
    if (nextErrors.length > 0) {
      setErrors(nextErrors);
      return;
    }
    setStep((current) => Math.min(current + 1, steps.length - 1));
  };

  const handleSubmit = async () => {
    const allErrors = [0, 2, 3].flatMap((index) =>
      validateMissionWizardStep(draft, index, launchSites),
    );
    if (allErrors.length > 0) {
      setErrors(allErrors);
      return;
    }
    await onSubmit(buildMissionWizardPayload(draft, launchSites, orbitPresets));
  };

  const stepContent = (() => {
    if (isLoadingCatalog) {
      return (
        <div className="flex min-h-80 items-center justify-center text-sm text-muted">
          Loading launch sites and mission catalog…
        </div>
      );
    }
    if (catalogError || launchSites.length === 0 || bodies.length === 0) {
      return (
        <div
          role="alert"
          className="mx-auto flex min-h-80 max-w-lg flex-col items-center justify-center text-center"
        >
          <AlertTriangle className="h-7 w-7 text-warning" />
          <h2 className="mt-3 text-base font-semibold text-text">
            Mission catalogue is unavailable
          </h2>
          <p className="mt-2 text-sm leading-relaxed text-muted">
            {catalogError ??
              'No launch sites or celestial bodies were returned. A mission cannot be created safely without valid catalogue data.'}
          </p>
          {onRetryCatalog && (
            <button
              type="button"
              onClick={onRetryCatalog}
              className="mt-4 flex items-center gap-2 rounded-control border border-border bg-surface px-3 py-2 text-xs text-text hover:bg-surface-sunken"
            >
              <RefreshCw className="h-3.5 w-3.5" /> Retry catalogue
            </button>
          )}
        </div>
      );
    }

    switch (step) {
      case 0:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">What is this mission for?</h2>
              <p className="mt-1 text-sm text-muted">
                Start with the goal. You can refine every technical value later.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="Mission name"
                hint="Use a memorable name, such as “Campus CubeSat 1” or “Lunar Ice Mapper”."
              >
                <input
                  autoFocus
                  value={draft.name}
                  maxLength={200}
                  onChange={(event) => patch({ name: event.target.value })}
                  className={inputClass}
                />
                <span className="text-right text-[10px] tabular-nums text-faint">
                  {draft.name.length}/200
                </span>
              </Field>
              <Field
                label="Short description"
                optional
                hint="Describe the science, engineering, or learning objective."
              >
                <textarea
                  value={draft.description}
                  maxLength={8000}
                  rows={3}
                  placeholder="What should this mission accomplish?"
                  onChange={(event) => patch({ description: event.target.value })}
                  className={`${inputClass} resize-none`}
                />
                <span className="text-right text-[10px] tabular-nums text-faint">
                  {draft.description.length.toLocaleString()}/8,000
                </span>
              </Field>
            </div>
            <FieldGroup
              label="Mission profile"
              hint="The profile sets the broad flight regime; it does not lock you into a specific orbit."
            >
              <div className="grid grid-cols-1 gap-2 sm:grid-cols-2 lg:grid-cols-3">
                {(Object.entries(missionTypeGuidance) as [
                  MissionType,
                  (typeof missionTypeGuidance)[MissionType],
                ][]).map(([value, guidance]) => (
                  <button
                    key={value}
                    type="button"
                    aria-pressed={draft.missionType === value}
                    onClick={() =>
                      patch({
                        missionType: value,
                        destinationBody: suggestedDestination(value),
                        orbitPresetId: '',
                      })
                    }
                    className={`rounded-panel border p-3 text-left transition-colors ${
                      draft.missionType === value
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-surface hover:border-border-strong'
                    }`}
                  >
                    <span className="block text-sm font-medium text-text">{guidance.label}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-faint">
                      {guidance.description}
                    </span>
                  </button>
                ))}
              </div>
            </FieldGroup>
            <LearningNote>
              An <strong>orbit</strong> is continuous free-fall around a body. Reaching space and
              reaching orbit are different: orbit also requires high horizontal speed.
            </LearningNote>
          </div>
        );

      case 1:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">Choose a destination</h2>
              <p className="mt-1 text-sm text-muted">
                Select where the vehicle is going and, for Earth missions, an optional target orbit.
              </p>
            </div>
            <FieldGroup label="Primary destination">
              <div className="grid grid-cols-2 gap-2 sm:grid-cols-3">
                {availableBodies.map((body) => (
                  <button
                    key={body.id}
                    type="button"
                    aria-pressed={draft.destinationBody === body.id}
                    onClick={() =>
                      patch({
                        destinationBody: body.id as BodyIdDto,
                        orbitPresetId: body.id === 'earth' ? draft.orbitPresetId : '',
                      })
                    }
                    className={`rounded-panel border p-3 text-left ${
                      draft.destinationBody === body.id
                        ? 'border-accent bg-accent/10'
                        : 'border-border bg-surface hover:border-border-strong'
                    }`}
                  >
                    <span className="text-sm font-medium text-text">{body.name}</span>
                    <span className="mt-1 block text-xs leading-relaxed text-faint">
                      {body.description}
                    </span>
                  </button>
                ))}
              </div>
            </FieldGroup>
            {draft.destinationBody === 'earth' ? (
              <Field
                label="Target orbit"
                optional
                hint="Skip this if you want to design the orbit later in Orbit Editor."
              >
                <select
                  value={draft.orbitPresetId}
                  onChange={(event) => patch({ orbitPresetId: event.target.value })}
                  className={inputClass}
                >
                  <option value="">Decide later — no target orbit yet</option>
                  {orbitPresets.map((preset) => (
                    <option key={preset.id} value={preset.id}>
                      {preset.name}
                    </option>
                  ))}
                </select>
                {selectedOrbit && (
                  <div className="rounded-control bg-surface-sunken p-3 text-xs leading-relaxed text-muted">
                    <strong className="text-text">{selectedOrbit.name}:</strong>{' '}
                    {selectedOrbit.description} Altitude{' '}
                    {(selectedOrbit.altitudeMeters / 1000).toLocaleString()} km
                    {selectedOrbit.inclinationDeg === null
                      ? '; inclination will be estimated from the launch site.'
                      : `; inclination ${selectedOrbit.inclinationDeg}°.`}
                  </div>
                )}
              </Field>
            ) : (
              <LearningNote>
                A detailed arrival orbit is optional during setup. Use Transfer Planner after
                creation to design departure, cruise, and capture burns.
              </LearningNote>
            )}
            <LearningNote>
              <strong>Altitude</strong> is height above a body’s surface.{' '}
              <strong>Inclination</strong> is the orbit’s tilt relative to its equator. A launch
              site’s latitude limits which inclinations are easy to reach.
            </LearningNote>
          </div>
        );

      case 2:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">Plan the launch</h2>
              <p className="mt-1 text-sm text-muted">
                Choose the spaceport and a working launch time. Both remain editable.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <Field
                label="Launch date and time (UTC)"
                hint="UTC is the shared world time used by mission teams; it avoids time-zone ambiguity."
              >
                <input
                  type="datetime-local"
                  value={draft.launchAtLocal}
                  onChange={(event) => patch({ launchAtLocal: event.target.value })}
                  className={inputClass}
                />
              </Field>
              <Field
                label="Launch site"
                hint="The site affects reachable inclination, Earth-rotation boost, and ground track."
              >
                <select
                  value={draft.launchSiteId}
                  onChange={(event) => patch({ launchSiteId: event.target.value })}
                  className={inputClass}
                >
                  <option value="">Choose a launch site</option>
                  {launchSites.map((site) => (
                    <option key={site.id} value={site.id}>
                      {site.facility} — {site.name}
                    </option>
                  ))}
                </select>
              </Field>
            </div>
            {selectedSite && (
              <div className="grid grid-cols-2 gap-3 rounded-panel border border-border bg-surface p-4 text-xs sm:grid-cols-4">
                <div>
                  <span className="block text-faint">Country</span>
                  <span className="text-text">{selectedSite.country}</span>
                </div>
                <div>
                  <span className="block text-faint">Latitude</span>
                  <span className="text-text">{selectedSite.latitudeDeg.toFixed(2)}°</span>
                </div>
                <div>
                  <span className="block text-faint">Launch azimuth</span>
                  <span className="text-text">
                    {selectedSite.azimuthRangeDeg[0]}–{selectedSite.azimuthRangeDeg[1]}°
                  </span>
                </div>
                <div>
                  <span className="block text-faint">Crew capable</span>
                  <span className="text-text">{selectedSite.crewRated ? 'Yes' : 'No'}</span>
                </div>
                {selectedSite.notes && (
                  <p className="col-span-full border-t border-border pt-3 leading-relaxed text-muted">
                    {selectedSite.notes}
                  </p>
                )}
              </div>
            )}
            <LearningNote>
              <strong>Launch azimuth</strong> is the compass direction flown after liftoff, measured
              clockwise from north. Safe corridors avoid populated areas and help set orbital
              inclination.
            </LearningNote>
          </div>
        );

      case 3:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">Define payload and crew</h2>
              <p className="mt-1 text-sm text-muted">
                Tell the simulator what the rocket carries. A vehicle can be linked now or later.
              </p>
            </div>
            <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
              <NumberField
                label="Payload mass (kg)"
                value={draft.payloadMassKg}
                min={0}
                max={10_000_000}
                hint="Payload includes the spacecraft, instruments, cargo, and deployed hardware."
                onChange={(payloadMassKg) => patch({ payloadMassKg })}
              />
              <Field
                label="Launch vehicle"
                optional
                hint="Choose one of your saved rockets, or design and link one later in Rocket Editor."
              >
                <select
                  value={draft.rocketId}
                  onChange={(event) => patch({ rocketId: event.target.value })}
                  className={inputClass}
                >
                  <option value="">Choose later — no rocket linked</option>
                  {rockets.map((rocket) => (
                    <option key={rocket.id} value={rocket.id}>
                      {rocket.name} · {rocket.stages.length} stage
                      {rocket.stages.length === 1 ? '' : 's'}
                    </option>
                  ))}
                </select>
              </Field>
              <Field label="Mission carries">
                <select
                  value={draft.crewKind}
                  onChange={(event) =>
                    patch({
                      crewKind: event.target.value as MissionWizardDraft['crewKind'],
                      crewCount: event.target.value === 'crew' ? Math.max(1, draft.crewCount) : 0,
                    })
                  }
                  className={inputClass}
                >
                  <option value="cargo">Cargo / uncrewed spacecraft</option>
                  <option value="crew">People (crewed mission)</option>
                </select>
              </Field>
              {draft.crewKind === 'crew' && (
                <NumberField
                  label="Crew count"
                  value={draft.crewCount}
                  min={1}
                  max={20}
                  step={1}
                  hint="Number of people aboard during launch."
                  onChange={(crewCount) => patch({ crewCount: Math.round(crewCount) })}
                />
              )}
              <Field
                label="Tags"
                optional
                hint="Comma-separated labels help organize missions, for example: student, cubesat, science."
              >
                <input
                  value={draft.tags}
                  placeholder="student, cubesat, science"
                  onChange={(event) => patch({ tags: event.target.value })}
                  className={inputClass}
                />
              </Field>
            </div>
            <LearningNote>
              <strong>Payload</strong> is everything the launch vehicle is meant to deliver. The
              rocket itself—including stages, engines, tanks, and propellant—is not payload.
            </LearningNote>
          </div>
        );

      case 4:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">Set launch conditions</h2>
              <p className="mt-1 text-sm text-muted">
                This entire step is optional. Standard atmosphere is suitable for an initial study.
              </p>
            </div>
            <label className="flex cursor-pointer items-start gap-3 rounded-panel border border-border bg-surface p-4">
              <input
                type="checkbox"
                checked={draft.weatherEnabled}
                onChange={(event) => patch({ weatherEnabled: event.target.checked })}
                className="mt-1 h-4 w-4 accent-accent"
              />
              <span>
                <span className="block text-sm font-medium text-text">Customize weather</span>
                <span className="mt-1 block text-xs leading-relaxed text-faint">
                  Include a deterministic weather profile in this mission. It is not a live
                  forecast, so simulations remain repeatable.
                </span>
              </span>
            </label>
            {draft.weatherEnabled ? (
              <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                <NumberField
                  label="Surface temperature (K)"
                  value={draft.weather.surfaceTemperatureK}
                  min={180}
                  max={340}
                  hint={`${(draft.weather.surfaceTemperatureK - 273.15).toFixed(1)} °C. Kelvin is the SI temperature scale used in physics.`}
                  onChange={(surfaceTemperatureK) =>
                    patch({ weather: { ...draft.weather, surfaceTemperatureK } })
                  }
                />
                <NumberField
                  label="Surface pressure (Pa)"
                  value={draft.weather.surfacePressurePa}
                  min={50_000}
                  max={110_000}
                  hint="101,325 Pa is standard sea-level pressure."
                  onChange={(surfacePressurePa) =>
                    patch({ weather: { ...draft.weather, surfacePressurePa } })
                  }
                />
                <NumberField
                  label="Surface wind speed (m/s)"
                  value={draft.weather.surfaceWindSpeed}
                  min={0}
                  max={60}
                  hint="Wind changes the air-relative speed and aerodynamic forces."
                  onChange={(surfaceWindSpeed) =>
                    patch({ weather: { ...draft.weather, surfaceWindSpeed } })
                  }
                />
                <NumberField
                  label="Wind direction (°)"
                  value={draft.weather.surfaceWindDirectionDeg}
                  min={0}
                  max={360}
                  hint="Direction the wind blows toward, clockwise from north."
                  onChange={(surfaceWindDirectionDeg) =>
                    patch({ weather: { ...draft.weather, surfaceWindDirectionDeg } })
                  }
                />
                <NumberField
                  label="Relative humidity"
                  value={draft.weather.relativeHumidity}
                  min={0}
                  max={1}
                  step={0.05}
                  hint="Use 0 for dry air and 1 for fully saturated air."
                  onChange={(relativeHumidity) =>
                    patch({ weather: { ...draft.weather, relativeHumidity } })
                  }
                />
                <NumberField
                  label="Peak jet-stream speed (m/s)"
                  value={draft.weather.jetStreamSpeed}
                  min={0}
                  max={120}
                  hint="High-altitude winds can create bending loads during ascent."
                  onChange={(jetStreamSpeed) =>
                    patch({ weather: { ...draft.weather, jetStreamSpeed } })
                  }
                />
              </div>
            ) : (
              <div className="rounded-panel border border-dashed border-border p-6 text-center text-sm text-faint">
                Standard atmosphere will be used. You can add detailed weather later in Inspector.
              </div>
            )}
            <LearningNote>
              <strong>Deterministic</strong> means the same inputs produce the same result every
              time. That makes experiments easier to compare and reproduce.
            </LearningNote>
          </div>
        );

      default:
        return (
          <div className="space-y-5">
            <div>
              <h2 className="text-lg font-semibold text-text">Review your mission</h2>
              <p className="mt-1 text-sm text-muted">
                Confirm the starting configuration. Nothing here is permanent—you can edit it later.
              </p>
            </div>
            <div className="rounded-panel border border-border bg-surface px-4">
              <SummaryRow label="Name" value={draft.name.trim()} />
              <SummaryRow
                label="Profile"
                value={missionTypeGuidance[draft.missionType].label}
              />
              <SummaryRow
                label="Destination"
                value={selectedBody?.name ?? draft.destinationBody}
              />
              <SummaryRow
                label="Target orbit"
                value={selectedOrbit?.name ?? 'Not set — configure later'}
              />
              <SummaryRow
                label="Launch"
                value={`${new Date(`${draft.launchAtLocal}:00Z`).toLocaleString()} · ${
                  selectedSite ? `${selectedSite.facility}, ${selectedSite.country}` : 'No site'
                }`}
              />
              <SummaryRow
                label="Payload"
                value={`${draft.payloadMassKg.toLocaleString()} kg · ${
                  draft.crewKind === 'crew' ? `${draft.crewCount} crew` : 'uncrewed'
                }`}
              />
              <SummaryRow
                label="Rocket"
                value={selectedRocket?.name ?? 'Not linked — choose later'}
              />
              <SummaryRow
                label="Weather"
                value={draft.weatherEnabled ? 'Custom deterministic profile' : 'Standard atmosphere'}
              />
            </div>
            <LearningNote>
              After creation, use <strong>Rocket Editor</strong> for vehicle stages,{' '}
              <strong>Orbit Editor</strong> for orbital elements and maneuvers, and{' '}
              <strong>Transfer Planner</strong> for travel between orbits or worlds.
            </LearningNote>
          </div>
        );
    }
  })();

  return (
    <Dialog
      open={open}
      onOpenChange={(nextOpen) => {
        if (!isSubmitting) onOpenChange(nextOpen);
      }}
      title="Create a mission"
      className="top-[4%] flex max-h-[92vh] max-w-5xl flex-col"
    >
      <header className="flex items-start justify-between border-b border-border px-5 py-4">
        <div>
          <p className="text-xs font-medium uppercase tracking-wider text-accent">
            Mission setup wizard
          </p>
          <p className="mt-1 text-sm text-muted">
            Step {step + 1} of {steps.length}: {steps[step].label}
          </p>
        </div>
        <button
          type="button"
          onClick={() => onOpenChange(false)}
          disabled={isSubmitting}
          aria-label="Close mission wizard"
          className="rounded-control p-1.5 text-faint hover:bg-surface-sunken hover:text-text disabled:opacity-40"
        >
          <X className="h-4 w-4" />
        </button>
      </header>

      <nav aria-label="Mission setup progress" className="border-b border-border px-5 py-3">
        <ol className="grid grid-cols-6 gap-1">
          {steps.map((item, index) => {
            const Icon = item.icon;
            const complete = index < step;
            const current = index === step;
            return (
              <li key={item.label}>
                <button
                  type="button"
                  disabled={index > step || isSubmitting}
                  onClick={() => {
                    if (index < step) {
                      setStep(index);
                      setErrors([]);
                    }
                  }}
                  aria-current={current ? 'step' : undefined}
                  className={`flex w-full flex-col items-center gap-1 rounded-control px-1 py-1.5 text-[10px] sm:flex-row sm:justify-center sm:text-xs ${
                    current
                      ? 'bg-accent/10 text-accent'
                      : complete
                        ? 'text-success hover:bg-surface-sunken'
                        : 'text-faint'
                  }`}
                >
                  {complete ? <Check className="h-3.5 w-3.5" /> : <Icon className="h-3.5 w-3.5" />}
                  <span className="hidden sm:inline">{item.label}</span>
                </button>
              </li>
            );
          })}
        </ol>
      </nav>

      <main className="min-h-0 flex-1 overflow-y-auto px-5 py-5">{stepContent}</main>

      <footer className="border-t border-border bg-surface-raised px-5 py-3">
        {errors.length > 0 && (
          <div role="alert" className="mb-3 rounded-control border border-danger/40 bg-danger/10 px-3 py-2 text-xs text-danger">
            {errors.map((error) => (
              <p key={error}>{error}</p>
            ))}
          </div>
        )}
        <div className="flex items-center justify-between">
          <button
            type="button"
            disabled={step === 0 || isSubmitting}
            onClick={() => {
              setStep((current) => Math.max(0, current - 1));
              setErrors([]);
            }}
            className="flex items-center gap-1 rounded-control border border-border px-3 py-2 text-xs text-muted hover:text-text disabled:opacity-30"
          >
            <ChevronLeft className="h-3.5 w-3.5" /> Back
          </button>
          <div className="flex items-center gap-2">
            {(step === 1 || step === 4) && (
              <button
                type="button"
                onClick={() => {
                  if (step === 1) patch({ orbitPresetId: '' });
                  if (step === 4) patch({ weatherEnabled: false });
                  goNext();
                }}
                className="px-3 py-2 text-xs text-faint hover:text-text"
              >
                Skip optional details
              </button>
            )}
            {step < steps.length - 1 ? (
              <button
                type="button"
                onClick={goNext}
                disabled={
                  isLoadingCatalog ||
                  Boolean(catalogError) ||
                  launchSites.length === 0 ||
                  bodies.length === 0
                }
                className="flex items-center gap-1 rounded-control bg-accent px-4 py-2 text-xs font-medium text-accent-contrast hover:brightness-110 disabled:opacity-40"
              >
                Continue <ChevronRight className="h-3.5 w-3.5" />
              </button>
            ) : (
              <button
                type="button"
                onClick={() => void handleSubmit()}
                disabled={isSubmitting}
                className="flex items-center gap-1.5 rounded-control bg-accent px-4 py-2 text-xs font-medium text-accent-contrast hover:brightness-110 disabled:opacity-50"
              >
                {isSubmitting ? 'Creating mission…' : 'Create mission'}
                {!isSubmitting && <Check className="h-3.5 w-3.5" />}
              </button>
            )}
          </div>
        </div>
      </footer>
    </Dialog>
  );
}
